import { useRef, useMemo, useCallback, useEffect } from "react";
import RcTable, { Summary } from "rc-table";
import { INTERNAL_HOOKS } from "rc-table/es/Table";
import { Spin } from "antd";
import scrollTo from "antd/es/_util/scrollTo";
import { getColumnKey } from "antd/es/table/util";
import useLazyKVMap from "antd/es/table/hooks/useLazyKVMap";
import useTitleColumns from "antd/es/table/hooks/useTitleColumns";
import useSorter, { getSortData } from "antd/es/table/hooks/useSorter";
import usePagination, { DEFAULT_PAGE_SIZE, getPaginationParam } from "antd/es/table/hooks/usePagination";
import useSelection, { SELECTION_ALL, SELECTION_INVERT, SELECTION_NONE } from "antd/es/table/hooks/useSelection";
import cx from "classnames";
import { omit } from "../../utils/object-util";
import { compose } from "../../utils/func-util";
import { isString, isFunction, isNumber, isUndefined } from "../../utils/type-util";
import { assignRef } from "../../utils/react-util";
import { useControllableState, useUpdateEffect } from "../../hooks";
import Empty from "../empty";
import Pagination from "../pagination";
import { Wrapper } from "./table.styles";
import { useFilter } from "./table-filter-column";
import TableToolbar from "./table-toolbar";
import TableBatchActions from "./table-batch-actions";
import TableActionButton from "./table-action-button";
import TableColumnConfig from "./table-column-config";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const defaultDataSource = [];
function Table(props) {
  const {
    size = "small",
    dataSource = defaultDataSource,
    filters,
    filtersDisabled,
    loading = false,
    bordered = false,
    pagination,
    rowSelection,
    rowKey = "key",
    rowClassName,
    columns = [],
    columnConfig,
    onColumnConfigChange,
    onChange,
    onFilter,
    getPopupContainer,
    expandable = {},
    scroll,
    sticky,
    fixedSummary,
    sortDirections,
    emptyText,
    title,
    toolbar,
    summary,
    bodyRef,
    className,
    style
  } = props;
  const {
    childrenColumnName = "children"
  } = expandable;
  const prefixCls = "ant-table";

  // ============================= Body =============================

  const ownBodyRef = useRef();
  useEffect(() => assignRef(bodyRef, ownBodyRef.current));
  useEffect(() => {
    if (ownBodyRef.current && scroll?.y) {
      ownBodyRef.current.style.minHeight = `${scroll.y}px`;
    }
  });

  // ============================ RowKey ============================

  const getRowKey = useMemo(() => {
    if (isFunction(rowKey)) {
      return rowKey;
    }
    return record => record?.[rowKey];
  }, [rowKey]);
  const [getRecordByKey] = useLazyKVMap(dataSource, childrenColumnName, getRowKey);

  // ============================ Events =============================

  const changeEventInfo = {};
  const triggerOnChange = function (info, action) {
    let reset = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    const changeInfo = {
      ...changeEventInfo,
      ...info
    };
    if (reset) {
      changeEventInfo.resetPagination();
      if (changeInfo.pagination.current) {
        changeInfo.pagination.current = 1;
      }
      if (pagination && pagination.onChange) {
        pagination.onChange(1, changeInfo.pagination.pageSize);
      }
    }
    if (scroll && scroll.scrollToFirstRowOnChange !== false && ownBodyRef.current) {
      scrollTo(0, {
        getContainer: () => ownBodyRef.current
      });
    }
    if (onChange) {
      onChange?.({
        sorter: changeInfo.sorter,
        pagination: changeInfo.pagination,
        currentDataSource: getSortData(dataSource, changeInfo.sorterStates, childrenColumnName),
        action
      });
    }
  };

  // ============================ Sorter =============================

  const onSorterChange = (sorter, sorterStates) => {
    triggerOnChange({
      sorter,
      sorterStates
    }, "sort", false);
  };
  const [transformSorterColumns, sortStates, sorterTitleProps, getSorters] = useSorter({
    prefixCls,
    mergedColumns: columns,
    onSorterChange,
    sortDirections: sortDirections ?? ["ascend", "descend"]
  });
  const sortedData = useMemo(() => getSortData(dataSource, sortStates, childrenColumnName), [dataSource, sortStates]);
  changeEventInfo.sorter = getSorters();
  changeEventInfo.sorterStates = sortStates;

  // ============================ Column =============================

  const [transformTitleColumns] = useTitleColumns(sorterTitleProps);
  const configColumnKeys = columns.filter(col => col.configurable).map((col, i) => getColumnKey(col, String(i)));
  const [visibleColumnKeys, setVisibleColumnKeys] = useControllableState(columnConfig, onColumnConfigChange, configColumnKeys);
  useUpdateEffect(() => {
    if (visibleColumnKeys.join() !== configColumnKeys.join()) {
      setVisibleColumnKeys(configColumnKeys);
    }
  }, [configColumnKeys.join()]);
  const visibleColumns = useMemo(() => columns.filter((col, i) => {
    return !col.configurable || visibleColumnKeys.includes(getColumnKey(col, String(i)));
  }).map(col => {
    return {
      ...col,
      dataIndex: isString(col.dataIndex) ? col.dataIndex.split(".") : col.dataIndex
    };
  }), [columns, visibleColumnKeys]);
  const columnConfigProps = {
    columns,
    value: visibleColumnKeys,
    onChange: setVisibleColumnKeys
  };

  // ========================== Pagination ===========================

  const onPaginationChange = (current, pageSize) => {
    triggerOnChange({
      pagination: {
        ...changeEventInfo.pagination,
        current,
        pageSize
      }
    }, "paginate");
  };
  const [mergedPagination, resetPagination] = usePagination(sortedData.length, pagination, onPaginationChange);
  changeEventInfo.pagination = pagination === false ? {} : getPaginationParam(pagination, mergedPagination);
  changeEventInfo.resetPagination = resetPagination;

  // ============================= Data ==============================

  const pageData = useMemo(() => {
    if (pagination === false || !mergedPagination.pageSize) {
      return sortedData;
    }
    const {
      current = 1,
      total,
      pageSize = DEFAULT_PAGE_SIZE
    } = mergedPagination;
    if (sortedData.length < total) {
      if (sortedData.length > pageSize) {
        return sortedData.slice((current - 1) * pageSize, current * pageSize);
      }
      return sortedData;
    }
    return sortedData.slice((current - 1) * pageSize, current * pageSize);
  }, [!!pagination, sortedData, mergedPagination?.current, mergedPagination?.pageSize, mergedPagination?.total]);

  // ========================== Expandable ===========================

  const expandType = useMemo(() => {
    if (dataSource.some(item => item?.[childrenColumnName])) {
      return "nest";
    }
    if (expandable?.expandedRowRender) {
      return "row";
    }
    return null;
  }, [dataSource]);

  // Pass origin render status into rc-table, this can be removed after refactor with rc-table
  expandable.__PARENT_RENDER_ICON__ = expandable.expandIcon;
  expandable.expandIcon = expandable.expandIcon ?? renderExpandIcon;
  if (expandType === "nest" && isUndefined(expandable.expandIconColumnIndex)) {
    expandable.expandIconColumnIndex = rowSelection ? 1 : 0;
  } else if (expandable.expandIconColumnIndex > 0 && rowSelection) {
    expandable.expandIconColumnIndex -= 1;
  }
  if (!isNumber(expandable.indentSize)) {
    expandable.indentSize = 15;
  }

  // ========================== Selections ===========================

  const [transformSelectionColumns, selectedKeySet] = useSelection(rowSelection, {
    prefixCls,
    data: sortedData,
    pageData,
    getRowKey,
    getRecordByKey,
    expandType,
    childrenColumnName,
    getPopupContainer,
    locale: {
      selectionAll: "Select All",
      selectInvert: "Invert Current Page"
    }
  });
  const internalRowClassName = (record, index, indent) => {
    let mergedRowClassName;
    if (isFunction(rowClassName)) {
      mergedRowClassName = cx(rowClassName(record, index, indent));
    } else {
      mergedRowClassName = cx(rowClassName);
    }
    return cx(mergedRowClassName, {
      [`${prefixCls}-row-selected`]: selectedKeySet.has(getRowKey(record, index))
    });
  };

  // ============================ Filter =============================

  const [transformFilterColumns] = useFilter({
    filters,
    onFilter,
    disabled: filtersDisabled ?? selectedKeySet.size > 0
  });

  // ============================ Render =============================

  const transformColumns = useCallback(compose(transformTitleColumns, transformSelectionColumns, transformFilterColumns, transformSorterColumns), [transformTitleColumns, transformSelectionColumns, transformFilterColumns, transformSorterColumns]);
  const getPaginationSize = () => {
    if (mergedPagination.size) {
      return mergedPagination.size;
    } else {
      return size === "small" || size === "middle" ? "small" : undefined;
    }
  };
  const paginationNode = pagination !== false && mergedPagination?.total ? /*#__PURE__*/_jsx(Pagination, {
    ...mergedPagination,
    className: `${prefixCls}-pagination ${prefixCls}-pagination-right`,
    size: getPaginationSize()
  }) : null;
  const renderTitle = title || toolbar ? data => /*#__PURE__*/_jsxs(_Fragment, {
    children: [isFunction(title) ? title(data) : title, toolbar && toolbar({
      columnConfigProps
    })]
  }) : null;
  const columnHasSummary = columns.some(col => col.summary) ?? false;
  const renderSummary = summary || columnHasSummary ? data => /*#__PURE__*/_jsxs(Summary, {
    fixed: fixedSummary ?? (isNumber(sticky?.offsetSummary) ? "bottom" : false),
    children: [columnHasSummary && renderColumnSummary({
      data,
      expandable,
      rowSelection,
      columns: visibleColumns
    }), summary && summary(data)]
  }) : null;
  const empty = isString(emptyText) ? /*#__PURE__*/_jsx(Empty, {
    image: Empty.PRESENTED_IMAGE_SIMPLE,
    description: emptyText
  }) : emptyText ?? /*#__PURE__*/_jsx(Empty, {
    image: Empty.PRESENTED_IMAGE_SIMPLE
  });
  const classString = cx(className, `${prefixCls}-wrapper`);
  const tableProps = omit(props, ["className", "style", "columns", "summary"]);
  const tableClass = cx({
    [`${prefixCls}-middle`]: size === "middle",
    [`${prefixCls}-small`]: size === "small",
    [`${prefixCls}-bordered`]: bordered,
    [`${prefixCls}-empty`]: dataSource.length === 0
  });
  return /*#__PURE__*/_jsx(Wrapper, {
    className: classString,
    style: style,
    children: /*#__PURE__*/_jsxs(Spin, {
      spinning: !!loading,
      children: [/*#__PURE__*/_jsx(RcTable, {
        ...tableProps,
        prefixCls: prefixCls,
        className: tableClass,
        columns: visibleColumns,
        data: pageData,
        rowKey: getRowKey,
        rowClassName: internalRowClassName,
        expandable: expandable,
        emptyText: empty,
        title: renderTitle,
        summary: renderSummary,
        internalHooks: INTERNAL_HOOKS,
        transformColumns: transformColumns,
        internalRefs: {
          body: ownBodyRef
        }
      }), paginationNode]
    })
  });
}
function renderColumnSummary(_ref) {
  let {
    data,
    columns,
    expandable,
    rowSelection
  } = _ref;
  const cells = columns?.slice() ?? [];
  if (expandable?.expandedRowRender) {
    cells.unshift({
      key: "expand"
    });
  }
  if (rowSelection) {
    cells.unshift({
      key: "select"
    });
  }
  return /*#__PURE__*/_jsx(Summary.Row, {
    children: cells.map((col, i) => /*#__PURE__*/_jsx(Summary.Cell, {
      index: i,
      children: col.summary?.(data)
    }, getColumnKey(col, String(i))))
  });
}
function renderExpandIcon(_ref2) {
  let {
    prefixCls,
    onExpand,
    record,
    expanded,
    expandable
  } = _ref2;
  const iconPrefix = `${prefixCls}-row-expand-icon`;
  return /*#__PURE__*/_jsx("button", {
    type: "button",
    onClick: e => {
      onExpand(record, e);
      e.stopPropagation();
    },
    className: cx(iconPrefix, {
      [`${iconPrefix}-spaced`]: !expandable,
      [`${iconPrefix}-expanded`]: expandable && expanded,
      [`${iconPrefix}-collapsed`]: expandable && !expanded
    })
  });
}
export default Object.assign(Table, {
  SELECTION_ALL,
  SELECTION_INVERT,
  SELECTION_NONE,
  Toolbar: TableToolbar,
  BatchActions: TableBatchActions,
  ActionButton: TableActionButton,
  ColumnConfig: TableColumnConfig,
  Summary: Summary
});